import { message } from 'antd';
import { useCallback } from 'react';
import {
  updateAdminProductSizes,
  updateAdminProductVariantStock,
} from '../../services';
import type { FetchAdminProductsOptions, SizeOption, UseAdminProductCatalogArgs } from '../interfaces';
import {
  updateMaterialSizesInList,
  updateVariantStockInList,
} from './adminProductForm.utils';
import { getApiErrorMessage } from '../../utils/apiError';

type UseAdminProductStockActionsArgs = Pick<
  UseAdminProductCatalogArgs,
  | 'activeSearchTerm'
  | 'draftStockById'
  | 'savingStockId'
  | 'setBaseProducts'
  | 'setDraftStockById'
  | 'setProducts'
  | 'setSavingSizesId'
  | 'setSavingStockId'
> & {
  fetchProducts: (options?: FetchAdminProductsOptions) => Promise<void>;
};

export const useAdminProductStockActions = ({
  activeSearchTerm,
  draftStockById,
  fetchProducts,
  savingStockId,
  setBaseProducts,
  setDraftStockById,
  setProducts,
  setSavingSizesId,
  setSavingStockId,
}: UseAdminProductStockActionsArgs) => {
  const handleStockChange = useCallback(async (variantInventoryId: string, stockQuantity: number | null) => {
    const nextStock = Math.max(0, Number(stockQuantity || 0));

    try {
      setSavingStockId(variantInventoryId);
      await updateAdminProductVariantStock(variantInventoryId, nextStock);
      setBaseProducts((prev) => updateVariantStockInList(prev, variantInventoryId, nextStock));
      setProducts((prev) => updateVariantStockInList(prev, variantInventoryId, nextStock));
      message.success('Variant stock updated.');
    } catch (error) {
      message.error(getApiErrorMessage(error, 'Failed to update variant stock.'));
    } finally {
      setSavingStockId(null);
    }
  }, [setBaseProducts, setProducts, setSavingStockId]);

  const handleStockStep = useCallback((variantInventoryId: string, currentStock: number, delta: number) => {
    const nextStock = Math.max(0, currentStock + delta);

    setDraftStockById((prev) => ({ ...prev, [variantInventoryId]: nextStock }));
    void handleStockChange(variantInventoryId, nextStock);
  }, [handleStockChange, setDraftStockById]);

  const handleDraftStockInput = useCallback((variantInventoryId: string, value: number | null) => {
    setDraftStockById((prev) => ({
      ...prev,
      [variantInventoryId]: Math.max(0, Number(value || 0)),
    }));
  }, [setDraftStockById]);

  const commitDraftStock = useCallback((variantInventoryId: string, originalStock: number) => {
    const draftStock = draftStockById[variantInventoryId];
    const nextStock = typeof draftStock === 'number' ? draftStock : originalStock;

    if (nextStock === originalStock || savingStockId === variantInventoryId) {
      return;
    }

    void handleStockChange(variantInventoryId, nextStock);
  }, [draftStockById, handleStockChange, savingStockId]);

  const handleMaterialSizesChange = useCallback(async (materialPriceId: string, sizeIds: string[]) => {
    try {
      setSavingSizesId(materialPriceId);
      const response = await updateAdminProductSizes(materialPriceId, sizeIds);
      const nextSizes = response.updateMaterialPrice.sizes as SizeOption[];
      setBaseProducts((prev) => updateMaterialSizesInList(prev, materialPriceId, nextSizes));
      setProducts((prev) => updateMaterialSizesInList(prev, materialPriceId, nextSizes));
      await fetchProducts({ append: false, skip: 0, search: activeSearchTerm });
      message.success('Material sizes updated.');
    } catch (error) {
      message.error(getApiErrorMessage(error, 'Failed to update material sizes.'));
    } finally {
      setSavingSizesId(null);
    }
  }, [activeSearchTerm, fetchProducts, setBaseProducts, setProducts, setSavingSizesId]);

  return {
    commitDraftStock,
    handleDraftStockInput,
    handleMaterialSizesChange,
    handleStockStep,
  };
};
